import { useEffect, useEffectEvent, useId, useRef, useState } from 'react';

import { supabase } from '@/integrations/supabase/client';
import { isNativePlatform } from '@/lib/mobile/platform';
import { isDesktopPlatform } from '@/lib/desktop/bridge';
import { NATIVE_RESUME_EVENT } from '@/lib/mobile/resume-pipeline';
import {
  applyRealtimeAuthToken,
  clearRealtimeAuthPrimeCache,
  primeRealtimeAuthForPrivateChannel,
} from '@/hooks/realtime-auth-prime';
import {
  handleSubscribeStatus,
  scheduleSubscribeRetry,
  type Channel,
  type SubscribeContext,
} from '@/hooks/use-realtime-channel-status';

import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

// =============================================================================
// Types
// =============================================================================

export interface RealtimeSubscription {
  event: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  table: string;
  filter?: string;
}

export interface RealtimeBroadcastSubscription {
  event: string;
  onPayload: (payload: unknown) => void;
}

type RealtimeChannelConfig = NonNullable<Parameters<typeof supabase.channel>[1]>;

export interface UseRealtimeChannelOptions {
  /** Unique channel name (e.g., 'correspondence-${orgId}') */
  channelName: string;
  /** One or more table subscriptions for this channel */
  subscriptions?: RealtimeSubscription[];
  /** One or more broadcast subscriptions for this channel */
  broadcastSubscriptions?: RealtimeBroadcastSubscription[];
  /** Supabase channel config, e.g. `{ config: { private: true } }` */
  channelConfig?: RealtimeChannelConfig;
  /** Callback when any subscribed event fires */
  onPayload?: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void;
  /** Callback for subscribe lifecycle state changes */
  onStatus?: (status: string, error?: Error) => void;
  /**
   * Fired on every re-subscribe AFTER the first successful subscribe of a
   * channel lifetime — i.e. after a transient reconnect, a backoff retry, or a
   * post-token-refresh recovery, but NOT on the initial join. This is the point
   * to reconcile events missed while the socket was down: `postgres_changes` has
   * no replay, and the global query client disables refetch-on-reconnect, so a
   * missed revoke/update is otherwise permanent until navigation. Wrappers pass
   * their own invalidation routine here.
   */
  onResubscribed?: () => void;
  /**
   * Fired on the first successful subscribe for the hook's current subscription (its channel
   * name, bindings and config), after the checks that drop a callback from a superseded effect
   * or a torn-down channel (unlike `onStatus`). Not again for a retry, a rejoin or the hook being
   * switched off and on, which fire `onResubscribed`; once more after the subscription changes
   * or the hook mounts again. For a wrapper whose first reads can be answered before the channel
   * joins: a write in between reaches nothing until the next event, since `postgres_changes`
   * has no replay.
   */
  onFirstSubscribed?: () => void;
  /** Callback when the managed channel instance changes */
  onChannel?: (channel: ReturnType<typeof supabase.channel> | null) => void;
  /** Skip subscription when false (default: true) */
  enabled?: boolean;
}

// =============================================================================
// Helpers
// =============================================================================
// The subscribe-status classification + retry-scheduling machine
// (handleSubscribeStatus, scheduleSubscribeRetry, SubscribeContext) lives in
// use-realtime-channel-status.ts.

function isPrivateChannelConfig(channelConfig: RealtimeChannelConfig | undefined): boolean {
  return channelConfig?.config?.private === true;
}

// Module-scope so the re-arm updater is not a function nested inside the
// effect's auth-refresh callback chain (SonarCloud S2004 nesting depth).
const nextEpoch = (epoch: number): number => epoch + 1;

// =============================================================================
// Hook
// =============================================================================

/**
 * Shared hook for Supabase real-time channel lifecycle management.
 *
 * Handles:
 * - Channel creation, subscription, and cleanup
 * - JWT token priming for private channels and refresh via `setAuth()` on `TOKEN_REFRESHED`
 * - Exponential backoff reconnection on transient errors
 * - Sentry-aware error classification (auth / transient / persistent)
 *
 * Wrappers own their handler logic (debounce, mutation guards, etc.).
 * This hook only manages the channel lifecycle.
 */
export function useRealtimeChannel(options: UseRealtimeChannelOptions): void {
  const {
    broadcastSubscriptions = [],
    channelConfig,
    channelName,
    enabled = true,
    subscriptions = [],
  } = options;
  const ownerId = useId();
  const channelSequenceRef = useRef(0);
  const subscriptionIdentityRef = useRef('');

  // Stabilize onPayload via useEffectEvent — prevents re-subscription on callback change
  const stableOnPayload = useEffectEvent(
    (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
      options.onPayload?.(payload);
    },
  );
  const stableOnBroadcastPayload = useEffectEvent((event: string, payload: unknown) => {
    const subscription = options.broadcastSubscriptions?.find((sub) => sub.event === event);
    subscription?.onPayload(payload);
  });
  const stableOnStatus = useEffectEvent((status: string, error?: Error) => {
    options.onStatus?.(status, error);
  });
  const stableOnChannel = useEffectEvent((channel: ReturnType<typeof supabase.channel> | null) => {
    options.onChannel?.(channel);
  });
  const stableOnResubscribed = useEffectEvent(() => {
    options.onResubscribed?.();
  });
  const stableOnFirstSubscribed = useEffectEvent(() => {
    options.onFirstSubscribed?.();
  });

  // Serialize subscriptions for stable dependency comparison (sorted keys for determinism)
  const subscriptionsKey = JSON.stringify(
    subscriptions.map(({ event, filter, table }) => ({ event, filter, table })),
  );
  const broadcastSubscriptionsKey = JSON.stringify(
    broadcastSubscriptions.map(({ event }) => ({ event })),
  );
  const channelConfigKey = JSON.stringify(channelConfig ?? {});

  // Retry state refs
  const retryCountRef = useRef(0);
  // Has the "persistent failure" Sentry warning fired for the current channel
  // lifetime? Reset on SUBSCRIBED (and on effect entry) so a future failure
  // burst can re-emit the warning.
  const warnedRef = useRef(false);
  // Permanent tombstone of channels we've torn down: once a channel is removed
  // it is added here and never taken out, so ANY later status it emits (its
  // synchronous CLOSED echo, a late SUBSCRIBED, a trailing error) is ignored for
  // the rest of its life. Per-channel (WeakSet keyed on the channel object) so a
  // fresh channel that lives concurrently (StrictMode mount→cleanup→mount, rapid
  // dep changes) is never wrongly suppressed. WeakSet so a tombstoned channel is
  // GC-reclaimed once nothing else references it.
  const tearingDownChannelsRef = useRef<WeakSet<Channel>>(new WeakSet());
  // Whether the current channel lifetime has already had one successful
  // SUBSCRIBED. Distinguishes the initial join (no reconcile needed) from a
  // recovery rejoin (reconcile missed events). Reset when the subscription scope changes.
  const hasSubscribedOnceRef = useRef(false);
  // Generation counter — immune to the "cancelled ref reset" problem across effect lifetimes
  const generationRef = useRef(0);
  // Consecutive realtime.messages authorization denials for the current channel
  // lifetime (see MAX_UNAUTHORIZED_ATTEMPTS in use-realtime-channel-status.ts).
  const unauthorizedCountRef = useRef(0);
  // Retryable failures that count as evidence against the channel (see
  // MAX_RETRIES_BEFORE_WARN in use-realtime-channel-status.ts).
  const channelFailureCountRef = useRef(0);
  // True while the channel is parked on authorization denial: no channel, no
  // retry timer. The TOKEN_REFRESHED listener un-parks by bumping authEpoch,
  // which re-runs the lifecycle effect with fresh claims on the socket.
  const parkedRef = useRef(false);
  const [authEpoch, setAuthEpoch] = useState(0);

  // Bumped on a native foreground resume to force a full teardown + re-subscribe
  // of the channel. iOS freezes the WebView in background, so the realtime socket
  // can be dead on resume with no CHANNEL_ERROR ever firing; re-running the
  // lifecycle effect (via this dep) reuses the existing generation/teardown path
  // to establish a fresh channel on the just-refreshed JWT. Web never fires it.
  // While `enabled` is false the listener is detached, so a resume fired in that
  // window is intentionally dropped - the lifecycle effect re-subscribes fresh
  // when `enabled` flips back, so no rejoin is lost.
  const [resumeNonce, setResumeNonce] = useState(0);
  useEffect(() => {
    if (
      !enabled ||
      (!isNativePlatform() && !isDesktopPlatform()) ||
      typeof globalThis.addEventListener !== 'function'
    ) {
      return;
    }
    const handleResume = () => {
      setResumeNonce((nonce) => nonce + 1);
    };
    globalThis.addEventListener(NATIVE_RESUME_EVENT, handleResume);
    return () => {
      globalThis.removeEventListener(NATIVE_RESUME_EVENT, handleResume);
    };
  }, [enabled]);

  // Token refresh listener. Keeps the realtime client's JWT fresh; the channel
  // effect's own backoff retry (not this listener) re-opens a channel torn down
  // by an expired token, so recovery does not depend on catching this event at
  // any particular moment relative to the WS auth error.
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED' && session?.access_token) {
        void (async () => {
          try {
            await applyRealtimeAuthToken(session.access_token);
          } catch {
            clearRealtimeAuthPrimeCache();
            return;
          }
          // A channel parked on authorization denial re-subscribes here — the
          // refreshed claims are the only event that can change the RLS
          // verdict — never on a timer.
          if (parkedRef.current) {
            parkedRef.current = false;
            setAuthEpoch(nextEpoch);
          }
        })();
        return;
      }
      if (event === 'SIGNED_OUT') {
        clearRealtimeAuthPrimeCache();
      }
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, [enabled]);

  // Channel lifecycle
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const generation = ++generationRef.current;
    retryCountRef.current = 0;
    warnedRef.current = false;
    const subscriptionIdentity = JSON.stringify([
      channelName,
      subscriptionsKey,
      broadcastSubscriptionsKey,
      channelConfigKey,
    ]);
    if (subscriptionIdentityRef.current !== subscriptionIdentity) {
      hasSubscribedOnceRef.current = false;
      subscriptionIdentityRef.current = subscriptionIdentity;
    }
    unauthorizedCountRef.current = 0;
    channelFailureCountRef.current = 0;
    parkedRef.current = false;

    // Track current channel and retry timer — scoped to this effect lifetime
    let currentChannel: ReturnType<typeof supabase.channel> | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const usesPrivateChannel = isPrivateChannelConfig(channelConfig);

    function setRetryTimer(timer: ReturnType<typeof setTimeout>): void {
      retryTimer = timer;
    }

    function isSocketConnected(): boolean {
      return supabase.realtime.isConnected();
    }

    function removeChannel(channel: Channel): void {
      // Idempotent + tombstoning: the tombstone entry (added once, never removed)
      // suppresses every later echo from this channel — the synchronous CLOSED
      // that unsubscribe() re-fires (which would otherwise recurse to a stack
      // overflow — docs/solutions/runtime-errors/2026-06-25-realtime-channel-
      // teardown-recursion.md), and any late SUBSCRIBED / error that arrives
      // after the phx_leave ack settles. The prior version deleted the entry on
      // the ack's .finally, leaving a post-ack / pre-retry window where a late
      // status slipped through; tombstoning for the channel's whole life closes
      // it (a removed channel object is never re-subscribed).
      if (tearingDownChannelsRef.current.has(channel)) {
        return;
      }
      if (currentChannel === channel) {
        currentChannel = null;
        stableOnChannel(null);
      }
      tearingDownChannelsRef.current.add(channel);
      // Fire-and-forget the actual removal; suppression is the tombstone's job,
      // not the promise lifecycle's, so nothing to do on settle. Some test mocks
      // return undefined instead of a Promise — the optional chain covers both.
      (supabase.removeChannel(channel) as Promise<unknown> | undefined)?.catch(() => null);
    }

    function subscribeChannel(): void {
      if (generationRef.current !== generation) {
        return;
      }

      // Realtime memoizes topics, including channels whose leave is pending.
      // Postgres listeners belong to one subscription lifetime; their table
      // filters and RLS authorize reads. Broadcast topics must retain the exact
      // server/policy grammar and are owned by their shared room provider.
      const topic =
        subscriptions.length > 0 && broadcastSubscriptions.length === 0 && !usesPrivateChannel
          ? `${channelName}:${ownerId}:${++channelSequenceRef.current}`
          : channelName;
      const channel = channelConfig
        ? supabase.channel(topic, channelConfig)
        : supabase.channel(topic);
      // Track immediately before subscribe — prevents orphaned channels if cleanup races
      currentChannel = channel;
      stableOnChannel(channel);

      // Register all postgres_changes listeners
      for (const sub of subscriptions) {
        channel.on(
          'postgres_changes',
          { event: sub.event, schema: 'public', table: sub.table, filter: sub.filter },
          (payload) => {
            if (
              generationRef.current === generation &&
              !tearingDownChannelsRef.current.has(channel)
            ) {
              stableOnPayload(payload);
            }
          },
        );
      }

      for (const sub of broadcastSubscriptions) {
        channel.on('broadcast', { event: sub.event }, ({ payload }) => {
          if (
            generationRef.current === generation &&
            !tearingDownChannelsRef.current.has(channel)
          ) {
            stableOnBroadcastPayload(sub.event, payload);
          }
        });
      }

      const ctx: SubscribeContext = {
        channel,
        channelName,
        generation,
        generationRef,
        retryCountRef,
        warnedRef,
        hasSubscribedOnceRef,
        fireOnResubscribed: stableOnResubscribed,
        fireOnFirstSubscribed: stableOnFirstSubscribed,
        tearingDownChannelsRef,
        unauthorizedCountRef,
        channelFailureCountRef,
        isSocketConnected,
        markParked: () => {
          parkedRef.current = true;
        },
        setRetryTimer,
        createAndSubscribe,
        removeChannel,
      };

      channel.subscribe((status, err) => {
        if (generationRef.current !== generation) {
          removeChannel(channel);
          return;
        }
        stableOnStatus(status, err);
        handleSubscribeStatus(status, ctx, err);
      });
    }

    function createAndSubscribe(): void {
      if (!usesPrivateChannel) {
        subscribeChannel();
        return;
      }

      void (async () => {
        try {
          await primeRealtimeAuthForPrivateChannel();
        } catch (error) {
          if (generationRef.current === generation) {
            const primeError =
              error instanceof Error ? error : new Error('Failed to prime realtime auth');
            stableOnStatus('CHANNEL_ERROR', primeError);
            scheduleSubscribeRetry(
              {
                channelName,
                generation,
                generationRef,
                retryCountRef,
                warnedRef,
                channelFailureCountRef,
                isSocketConnected,
                setRetryTimer,
                createAndSubscribe,
              },
              'CHANNEL_ERROR',
              primeError,
              'auth-prime',
            );
          }
          return;
        }
        if (generationRef.current !== generation) {
          return;
        }
        subscribeChannel();
      })();
    }

    createAndSubscribe();

    return () => {
      generationRef.current += 1; // Invalidate this generation
      clearTimeout(retryTimer);
      if (currentChannel) {
        removeChannel(currentChannel);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- serialized keys stabilize subscription/config deps
  }, [
    channelName,
    subscriptionsKey,
    broadcastSubscriptionsKey,
    channelConfigKey,
    enabled,
    resumeNonce,
    authEpoch,
    ownerId,
  ]);
}
